import { useRequest } from "../../../hooks/useRequest";
import { useCallback, useEffect, useMemo } from "react";
import { InvestmentApiResponse } from "../types";
import routes from "../../../routes";

function useInvestmentsListForClient(
  clientId: string,
  fromDate: number,
  toDate: number,
) {
  const { fetch, response, error, loading } = useRequest();

  const fetchInvestmentDetails = useCallback(async () => {
    const params = new URLSearchParams({
      fromDate: fromDate.toString(),
      toDate: toDate.toString(),
    });

    await fetch(
      "GET",
      `${routes.api.investment.summary({ clientId })}?${params}`,
    );
  }, [fetch, clientId, fromDate, toDate]);

  useEffect(() => {
    if (clientId) {
      fetchInvestmentDetails();
    }
  }, [clientId, fromDate, toDate, fetchInvestmentDetails]);

  const investments = useMemo(() => {
    if (response?.data) {
      return response.data.map((investment: InvestmentApiResponse) => {
        const individualInvestments = Array.isArray(
          investment.individualInvestments,
        )
          ? investment.individualInvestments.map((indInv) => ({
              createdAt: indInv.createdAt,
              quantity: indInv.quantity,
              unit: indInv.unit,
              note: indInv?.note || "",
              investmentId: indInv.investmentId,
            }))
          : [];

        return {
          projectName: investment.projectName,
          unit: investment.unit,
          totalInvested: investment.totalInvested,
          solutionId: investment.solutionId,
          createdAt: investment.createdAt,
          individualInvestments:
            individualInvestments.length > 0
              ? individualInvestments
              : [
                  {
                    createdAt: investment.createdAt,
                    quantity: investment.totalInvested,
                    unit: investment.unit,
                  },
                ],
        };
      });
    }
    return [];
  }, [response]);

  return {
    investments,
    error,
    loading,
    fetchInvestmentDetails,
  };
}

export default useInvestmentsListForClient;
